import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export const AuthLayout = ({ title, children, testid }) => (
  <div className="max-w-md mx-auto px-6 py-14" data-testid={testid}>
    <div className="bg-white rounded-2xl border border-slate-100 p-8 md:p-10 shadow-[0_12px_40px_rgba(15,41,66,0.08)]">
      <div className="flex items-center gap-2 mb-8">
        <img src="/icons/icon-192.png" alt="Boleia Connect" className="w-9 h-9 rounded-xl" />
        <span className="font-heading font-bold text-primary">Boleia<span className="text-accent"> Connect</span></span>
      </div>
      <h1 className="text-2xl md:text-3xl font-bold text-primary mb-6">{title}</h1>
      {children}
    </div>
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate(params.get("next") || "/painel");
    } catch (err) {
      setError(formatApiError(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={t("auth.loginTitle")} testid="login-page">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <PasswordInput id="password" data-testid="login-password-input" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 rounded-xl" />
          <div className="text-right mt-1.5">
            <Link to="/recuperar-palavra-passe" data-testid="login-forgot-password" className="text-xs text-accent font-semibold hover:underline">{t("auth.forgot")}</Link>
          </div>
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold">{t("auth.loginBtn")}</Button>
      </form>
      <p className="text-sm text-slate-500 mt-6 text-center">
        {t("auth.noAccount")}{" "}
        <Link to="/registar" data-testid="login-to-register" className="text-accent font-semibold hover:underline">{t("auth.registerTitle")}</Link>
      </p>
    </AuthLayout>
  );
}
